import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { useLazyGetAccountInvoicesByAgingQuery } from 'api/accounts/getAccountInvoicesByAging'
import { useRegenerateInvoiceListMutation } from 'api/billing/regenerateInvoiceList'
import { CACHE_TAG_ACCOUNT_INVOICE_LIST, CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY } from 'api/cacheTagTypes'
import { canUpdateInvoices } from 'data/permissions/permissionsSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import { triggerDraftRegenerationPolling } from 'slices/billing/invoicesSlice'
import T from 'T'
import { handleError } from 'utils/error'

// Aging bucket whose invoices are still in draft (status = Partial). Matches the backend Aging enum.
const DRAFT_AGING = 'DRAFT'
// Mirrors the manual regenerate flow (useInvoiceActionsCore): show the success toast for a few
// seconds, then refresh so the recalculated due dates are picked up once regeneration completes.
const REGENERATE_REFRESH_TIMEOUT_MS = 4000

/**
 * Shared logic for prompting the user to regenerate an account's draft invoices after a payment
 * terms / billing profile change, so the new due-date configuration takes effect on those drafts.
 *
 * Gated on the INVOICES:UPDATE permission — callers that can't regenerate never see the prompt.
 *
 * `maybePromptRegenerate` fetches the account's draft invoices and opens the confirmation prompt
 * when there are any; it resolves to `true` when the prompt was opened (so the caller can defer its
 * own success UI) and `false` otherwise. Spread `regenerateDraftDialogProps` into
 * `RegenerateDraftInvoicesDialog`.
 */
export const useRegenerateDraftInvoicesPrompt = (accountId: string) => {
  const dispatch = useDispatch()
  const canRegenerateInvoices = useSelector(canUpdateInvoices)
  const [getDraftInvoices] = useLazyGetAccountInvoicesByAgingQuery()
  const [regenerateInvoiceList, { isLoading: isRegenerating }] = useRegenerateInvoiceListMutation()
  const [draftInvoiceIds, setDraftInvoiceIds] = useState<string[]>([])
  const [isPromptOpen, setIsPromptOpen] = useState(false)

  const maybePromptRegenerate = useCallback(async () => {
    if (!canRegenerateInvoices) return false
    try {
      const response = await getDraftInvoices({ accountId, aging: DRAFT_AGING }).unwrap()
      const invoiceIds = (response.accountInvoices ?? []).map(invoice => invoice.id)
      if (invoiceIds.length === 0) return false
      setDraftInvoiceIds(invoiceIds)
      setIsPromptOpen(true)
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }, [accountId, canRegenerateInvoices, getDraftInvoices])

  const handleClosePrompt = useCallback(() => setIsPromptOpen(false), [])

  const handleConfirmRegenerate = useCallback(async () => {
    try {
      await regenerateInvoiceList({ invoiceIds: draftInvoiceIds, includeLateFees: false }).unwrap()
      setIsPromptOpen(false)
      toast.success(T.REGENERATE_INVOICES_SUCCESS_MSG, {
        hideProgressBar: false,
        autoClose: REGENERATE_REFRESH_TIMEOUT_MS,
        onClose: () => {
          dispatch(getCustomerDetails({ accountId }))
          dispatch(api.util.invalidateTags([CACHE_TAG_ACCOUNT_INVOICE_LIST, CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY]))
          // Re-arm the invoice list's generation polling (resets a prior exhausted cycle) so the
          // per-invoice progress bar shows on every regeneration, like the manual regenerate flow.
          // (Cast: createDataGridSlice is untyped JS, so TS can't see this action creator's signature.)
          dispatch((triggerDraftRegenerationPolling as (id: string) => { type: string; payload: string })(accountId))
        },
      })
    } catch (error) {
      handleError(error)
    }
  }, [accountId, dispatch, draftInvoiceIds, regenerateInvoiceList])

  return {
    maybePromptRegenerate,
    regenerateDraftDialogProps: {
      isOpen: isPromptOpen,
      isLoading: isRegenerating,
      onConfirm: handleConfirmRegenerate,
      onClose: handleClosePrompt,
    },
  }
}
